import { useEffect, useRef, useState } from 'react'

import { TransactionsHistory } from 'components/ui/transaction-history'

import { useAppSelector } from 'store/hooks'
import { selectedUser } from 'store/reducers/users'

import {
  ETransactionStatuses,
  ETransactionTypes,
  ITransaction,
  TransactionAccountType,
} from '../../../../../../store/fetchers/users/types'
import {
  TransactionTypeView,
  TransactionStatusView,
} from '../../../../../../components/ui/transaction-history/components/transaction/enum'
import { IAmountAndCoin } from '../../../../wallets/info/components/tab-transactions'

import { TransactionMain } from '../../../../../../components/ui/transaction-history/components/transaction'

import { fetchTransactions } from '../../../../../../store/fetchers/users'

import styles from './styles.module.scss'

import { exchangesSaveCSV } from './utils'

export const matchType = (type: ETransactionTypes, accountType: TransactionAccountType): TransactionTypeView => {
  if (type === ETransactionTypes.DEPOSIT && accountType === 'loan') {
    return TransactionTypeView.DEPOSIT
  }

  if (type === ETransactionTypes.DEPOSIT && accountType === 'wallet') {
    return TransactionTypeView.TOP_UP
  }

  if (type === ETransactionTypes.REPAYMENT) {
    return TransactionTypeView.REPAYMENT
  }

  if (type === ETransactionTypes.INTEREST) {
    return TransactionTypeView.INTEREST
  }

  if (type === ETransactionTypes.WITHDRAW && accountType === 'loan') {
    return TransactionTypeView.LOAN
  }

  if (type === ETransactionTypes.WITHDRAW && accountType === 'wallet') {
    return TransactionTypeView.WITHDRAWAL
  }

  if (type === ETransactionTypes.PLEDGE) {
    return TransactionTypeView.REDEMPTION
  }

  if (type === ETransactionTypes.EXCHANGE) {
    return TransactionTypeView.EXCHANGE
  }

  return null
}

export const matchStatus = (status: ETransactionStatuses): TransactionStatusView => {
  switch (status.toLowerCase()) {
    case ETransactionStatuses.PENDING:
      return TransactionStatusView.PENDING
    case ETransactionStatuses.CONFIRMING:
      return TransactionStatusView.CONFIRMING
    case ETransactionStatuses.PROCESSING:
    case ETransactionStatuses.REJECTED:
      return TransactionStatusView.PROCESSING
    case ETransactionStatuses.FINISHED:
      return TransactionStatusView.SUCCESS
    case ETransactionStatuses.REFUNDED:
      return TransactionStatusView.REFUNDED
    default:
      return null
  }
}

export const matchAmountAndCoin = (tx: ITransaction): IAmountAndCoin => {
  if (tx.transaction_type === ETransactionTypes.DEPOSIT) {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (
    tx.transaction_type === ETransactionTypes.REPAYMENT &&
    (tx.account_type === 'loan' || tx.account_type === 'saving')
  ) {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.REPAYMENT && tx.account_type === 'wallet') {
    return {
      amount: tx.from_amount,
      code: tx.from_currency,
      network: tx.from_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'loan') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'saving') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.WITHDRAW && tx.account_type === 'loan') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'saving') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.PLEDGE) {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  return {
    amount: tx.from_amount,
    code: tx.from_currency,
    network: tx.from_network,
    incoming: true,
  }
}

export function TransactionTab() {
  const user = useAppSelector(selectedUser)
  const [isLoading, setIsLoading] = useState(true)
  const [transactions, setTransactions] = useState<ITransaction[]>([])
  const isFetched = useRef<boolean>(false)
  const intervalRef = useRef(null)
  const [search, setSearch] = useState<string>('')

  const isTransactionsFetched = useRef(false)

  useEffect(() => {
    if (user && !isFetched.current) {
      const now = new Date()

      // Вычисляем количество миллисекунд до ближайших 10 секунд
      const delay = 60000 - (now.getTime() % 60000)
      const initialTimeout = setTimeout(() => {
        intervalRef.current = setInterval(fetchSavingsTransactionsWithoutLoader, 60000)
      }, delay)

      return () => {
        clearTimeout(initialTimeout)
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    }
    return undefined
  }, [user])

  useEffect(() => {
    if (!isTransactionsFetched.current && user) {
      fetchSavingsTransactions()
      isTransactionsFetched.current = true
    }
  }, [user])

  const fileNameCSV = `CoinRabbit_Exchange_history.csv`

  const fetchSavingsTransactions = async () => {
    setIsLoading(true)
    const res = await fetchTransactions({
      accountType: 'wallet',
      transactionType: 'exchange',
      search,
    })
    setTransactions(res)
    setIsLoading(false)
  }

  const fetchSavingsTransactionsWithoutLoader = async () => {
    const res = await fetchTransactions({
      accountType: 'wallet',
      transactionType: 'exchange',
      search,
    })
    setTransactions(res)
  }

  const handleSearchClick = () => {
    fetchSavingsTransactions()
  }

  const updatedList: TransactionMain[] = transactions
    .map((tx) => {
      const type = matchType(tx.transaction_type, tx.account_type)
      const status = matchStatus(tx.status)

      const amountAndCoin = matchAmountAndCoin(tx)

      if (
        type === TransactionTypeView.EXCHANGE ||
        tx.from_amount !== tx.to_amount ||
        tx.from_currency !== tx.to_currency ||
        tx.from_network !== tx.to_network
      ) {
        return {
          code: amountAndCoin.code,
          network: amountAndCoin.network,
          amount: amountAndCoin.amount,
          type: type,
          status: status,
          hash: tx.hash,
          label: type,
          date: tx.created_at,
          incoming: amountAndCoin.incoming,
          from_currency: {
            code: tx.from_currency,
            network: tx.from_network,
          },
          to_currency: {
            code: tx.to_currency,
            network: tx.to_network,
          },
          amountFrom: tx.from_amount,
          amountTo: tx.to_amount,
          id: tx.transaction_id.toString(),
        }
      } else
        return {
          amount: amountAndCoin.amount,
          code: amountAndCoin.code,
          network: amountAndCoin.network,
          type: type,
          status: status,
          hash: tx.hash,
          label: type,
          date: tx.created_at,
          incoming: amountAndCoin.incoming,
          id: tx.transaction_id.toString(),
        }
    })
    .filter((tx) => tx !== null && tx?.status !== null && tx?.type !== null)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  return (
    <div className={styles.wrapper}>
      <TransactionsHistory
        isLoading={isLoading}
        isHideTypeFilter
        isHideAmountSorting
        saveCSVProp={() => exchangesSaveCSV(updatedList, fileNameCSV)}
        list={updatedList}
        fileNameCSV={fileNameCSV}
        search={search}
        setSearch={setSearch}
        onSearchClick={handleSearchClick}
      />
    </div>
  )
}
